import moment from 'moment';
import React, { useReducer } from 'react';
import { createContext } from 'use-context-selector';
import axiosInstance from '../../services/axios';

import {
  BirthdateSelectPayload,
  CountrySelectedPayload,
  CurrentWorkStatusSelectedPayload,
  DrivingLicencesSelectedPayload,
  EducationSelectedPayload,
  EmailEnteredPayload,
  LanguageLevelsSelectedPayload,
  ErrorPayload,
  ExpectedSalarySelectedPayload,
  GenderSelectPayload,
  LocationSelectedPayload,
  MilitaryStatusSelectedPayload,
  NameOrSurnameEnteredPayload,
  PersonInfosSubmitErrorPayload,
  PersonalInfosSubmittedPayload,
  PhoneEnteredPayload,
  SMSCodeEnteredPayload,
  ShowOnboardingPayload,
  Types,
  UserAction,
  UserContext,
  UserGeolocationChangePayload,
  UserState,
  WorkTimePreferencesSelectedPayload,
} from './types';
import {
  isFieldRequired,
  validateEmailInput,
  validateFullnameInput,
  validatePhoneInput,
} from './utils';
import { Gender } from '../../types/gender';
import { UserAPI } from '../../services/user/api';
import { DEFAULT_PHONE_COUNTRY_CODE } from '../../constants';

const initialState: UserContext = {
  state: {
    phoneNumber: '',
    country: 'TR',
    verificationCode: '',
    loading: false,
    showVerification: false,
    showEmailCheck: false,
    showRegister: false,
    showQuestions: false,
    isRegistering: false,
    birthDate: moment(new Date()).subtract(15, 'years'),
    showOnboarding: false,
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  dispatch: () => {},
};
const userStore = createContext<UserContext>(initialState);
const { Provider } = userStore;
const api = new UserAPI();

const UserStateProvider = ({ children }: { children: React.ReactNode }) => {
  const submitPhone = async (phoneNumber: string, country: string): Promise<void> => {
    try {
      await api.checkPhone(phoneNumber, DEFAULT_PHONE_COUNTRY_CODE);

      // if (result.payload.message === PhoneResponseMessage.NOT_WORKER) {
      //   dispatch({ type: Types.PhoneSubmissionError, payload: { error: 'phoneCheck.notWorker' } });
      //   return;
      // }

      // if (result.payload.message === PhoneResponseMessage.VERIFIED_PHONE_NOT_FOUND) {
      //   dispatch({ type: Types.ShowEmailCheck, payload: {} });
      //   return;
      // }

      dispatch({ type: Types.ShowVerification, payload: {} });
      return;

      // dispatch({
      //   type: Types.PhoneSubmissionError,
      //   payload: { error: 'common.somethingWentWrong' },
      // });
    } catch (e) {
      if (e instanceof Error) {
        dispatch({ type: Types.PhoneSubmissionError, payload: { error: e.message } });
        return;
      }
    }
    dispatch({ type: Types.PhoneSubmissionError, payload: { error: 'common.somethingWentWrong' } });
  };

  const submitEmail = async (email: string): Promise<void> => {
    try {
      await api.checkEmail(email);

      // if (result.payload.message === EmailResponseMessage.NON_VERIFIED_USER) {
      //   dispatch({ type: Types.ShowVerification, payload: {} });
      //   return;
      // }

      // if (result.payload.message === EmailResponseMessage.EMAIL_IN_USE) {
      //   dispatch({ type: Types.EmailSubmissionError, payload: { error: 'emailCheck.emailInUse' } });
      //   return;
      // }

      dispatch({ type: Types.ShowRegister, payload: {} });
      return;
    } catch (e) {
      if (e instanceof Error) {
        dispatch({ type: Types.EmailSubmissionError, payload: { error: e.message } });
        return;
      }
    }
    dispatch({
      type: Types.EmailSubmissionError,
      payload: { error: 'common.somethingWentWrong' },
    });
  };

  const generateSecurePassword = (): string => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';

    // Generate a 12-character password with at least one of each type
    password += charset.charAt(Math.floor(Math.random() * 26)); // uppercase
    password += charset.charAt(26 + Math.floor(Math.random() * 26)); // lowercase
    password += charset.charAt(52 + Math.floor(Math.random() * 10)); // number
    password += charset.charAt(62 + Math.floor(Math.random() * 8)); // special char

    // Fill the rest with random characters
    for (let i = 4; i < 12; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    // Shuffle the password
    return password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');
  };

  const registerUser = async ({
    email,
    phoneNumber,
    firstName,
    lastName,
    gender,
    birthDate,
    latLon,
  }: UserState): Promise<void> => {
    try {
      if (!email || !firstName || !lastName || !gender || !latLon) {
        dispatch({
          type: Types.RegisterSubmissionError,
          payload: { error: 'common.somethingWentWrong' },
        });

        return;
      }

      const result = await api.registerUser(
        email,
        firstName,
        lastName,
        generateSecurePassword(),
        phoneNumber,
        birthDate.format('YYYY-MM-DD'),
        gender,
        `${latLon.lat}, ${latLon.lon}`,
      );

      dispatch({ type: Types.ShowOnboarding, payload: { result } });
      return;
    } catch (e) {
      if (e instanceof Error) {
        dispatch({ type: Types.RegisterSubmissionError, payload: { error: e.message } });
        return;
      }

      dispatch({
        type: Types.RegisterSubmissionError,
        payload: { error: 'common.somethingWentWrong' },
      });
    }

    dispatch({
      type: Types.RegisterSubmissionError,
      payload: { error: 'common.somethingWentWrong' },
    });
  };

  const submitVerificationCode = async (
    verificationCode: string,
    phoneNumber: string,
    country: string,
  ): Promise<void> => {
    try {
      const result = await api.submitVerificationCode(
        verificationCode,
        phoneNumber,
        DEFAULT_PHONE_COUNTRY_CODE,
      );

      if (result.payload?.action === 'register') {
        dispatch({ type: Types.ShowEmailCheck, payload: {} });
        return;
      }

      if (result.payload?.action === 'login') {
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${result.payload?.jwtToken}`;

        dispatch({ type: Types.ShowQuestions, payload: {} });
        return;
      }
    } catch (e) {
      if (e instanceof Error) {
        dispatch({ type: Types.SMSCodeSubmissionError, payload: { error: e.message } });
        return;
      }
    }

    dispatch({
      type: Types.SMSCodeSubmissionError,
      payload: { error: 'common.somethingWentWrong' },
    });
  };

  const updateUser = async ({
    firstName,
    email,
    lastName,
    workTimePreferences,
    currentWorkStatus,
    drivingLicences,
    militaryStatus,
    education,
    expectedSalary,
    languageLevels,
  }: UserState) => {
    try {
      if (!firstName || !email || !lastName) {
        dispatch({
          type: Types.PersonInfosSubmitError,
          payload: { error: 'common.somethingWentWrong' },
        });
        return;
      }

      await api.updateUser({
        workTimePreferences: workTimePreferences,
        unemployedStatus: !currentWorkStatus,
        drivingLicenses: drivingLicences,
        militaryStatus: militaryStatus,
        educationStatus: education,
        expectedSalary: expectedSalary,
        languages: languageLevels,
      });

      dispatch({ type: Types.ShowQuestions, payload: {} });
    } catch (e) {
      if (e instanceof Error) {
        dispatch({ type: Types.PersonInfosSubmitError, payload: { error: e.message } });
        return;
      }

      dispatch({
        type: Types.PersonInfosSubmitError,
        payload: { error: 'common.somethingWentWrong' },
      });
    }
  };

  const [state, dispatch] = useReducer((state: UserState, action: UserAction) => {
    let newState: UserState;
    switch (action.type) {
      case Types.BirthdateSelect: {
        const selectedBirthDate = (action.payload as BirthdateSelectPayload).birthdate;

        if (selectedBirthDate.isAfter(moment().subtract(16, 'years'))) {
          newState = {
            ...state,
            birthDate: selectedBirthDate,
            registerError: 'register.birthdateTooYoung',
          };

          return newState;
        }

        newState = {
          ...state,
          birthDate: selectedBirthDate,
          registerError: undefined,
        };

        return newState;
      }
      case Types.GenderSelect: {
        newState = {
          ...state,
          gender: (action.payload as GenderSelectPayload).gender,
        };

        return newState;
      }
      case Types.CountrySelected: {
        newState = { ...state, country: (action.payload as CountrySelectedPayload).country };

        return newState;
      }
      case Types.PhoneEntered: {
        newState = {
          ...state,
          phoneNumber: (action.payload as PhoneEnteredPayload).phoneNumber,
        };

        return newState;
      }
      case Types.EmailEntered: {
        newState = {
          ...state,
          email: (action.payload as EmailEnteredPayload).email,
        };

        return newState;
      }
      case Types.FirstNameEntered: {
        newState = {
          ...state,
          firstName: (action.payload as NameOrSurnameEnteredPayload).value,
        };
        return newState;
      }
      case Types.LastNameEntered: {
        newState = {
          ...state,
          lastName: (action.payload as NameOrSurnameEnteredPayload).value,
        };
        return newState;
      }
      case Types.PhoneSubmitted: {
        const { phoneError } = validatePhoneInput(state.phoneNumber, state.country);

        if (!phoneError) {
          newState = {
            ...state,
            loading: true,
            phoneError,
          };

          submitPhone(state.phoneNumber, state.country);

          return newState;
        }

        newState = {
          ...state,
          phoneError,
        };

        return newState;
      }
      case Types.EmailSubmitted: {
        const { emailError } = validateEmailInput(state.email ?? '');

        if (!emailError && state.email) {
          newState = {
            ...state,
            loading: true,
            emailError,
          };

          submitEmail(state.email);

          return newState;
        }

        newState = {
          ...state,
          emailError,
        };

        return newState;
      }
      case Types.ShowVerification: {
        newState = {
          ...state,
          showVerification: true,
          loading: false,
        };

        return newState;
      }
      case Types.SMSCodeEntered: {
        newState = {
          ...state,
          verificationCode: (action.payload as SMSCodeEnteredPayload).code,
        };

        return newState;
      }
      case Types.SMSCodeSubmitted: {
        if (state.verificationReregister) {
          newState = {
            ...state,
            loading: true,
          };

          registerUser(state);

          return newState;
        }

        if (state.isRegistering) {
          newState = {
            ...state,
            loading: false,
            showRegister: true,
            showVerification: false,
          };
          return newState;
        }
        newState = {
          ...state,
          loading: true,
        };
        submitVerificationCode(state.verificationCode, state.phoneNumber, state.country);

        return newState;
      }
      case Types.PhoneSubmissionError: {
        newState = {
          ...state,
          loading: false,
          phoneSubmissionError: (action.payload as ErrorPayload).error,
        };

        return newState;
      }
      case Types.EmailSubmissionError: {
        newState = {
          ...state,
          loading: false,
          emailSubmissionError: (action.payload as ErrorPayload).error,
        };

        return newState;
      }
      case Types.SMSCodeSubmissionError: {
        newState = {
          ...state,
          loading: false,
          verificationError: (action.payload as ErrorPayload).error,
        };

        return newState;
      }
      case Types.ShowEmailCheck: {
        newState = {
          ...state,
          showRegister: false,
          showVerification: false,
          showEmailCheck: true,
          showQuestions: false,
          showOnboarding: false,
          loading: false,
        };

        return newState;
      }
      case Types.ShowRegister: {
        newState = {
          ...state,
          showRegister: true,
          showVerification: false,
          showEmailCheck: false,
          showQuestions: false,
          showOnboarding: false,
          loading: false,
        };

        return newState;
      }
      case Types.ShowQuestions: {
        newState = {
          ...state,
          showQuestions: true,
          loading: false,
        };

        return newState;
      }
      case Types.ShowVerificationThenNameSurname: {
        newState = {
          ...state,
          showVerification: true,
          isRegistering: true,
          loading: false,
        };
        return newState;
      }
      case Types.RegisterSubmitted: {
        const { fullnameError } = validateFullnameInput(
          state.firstName ?? '',
          state.lastName ?? '',
        );

        if (!state.gender) {
          newState = {
            ...state,
            registerError: 'register.genderRequired',
          };

          return newState;
        }

        if (!state.latLon) {
          newState = {
            ...state,
            registerError: 'register.locationRequired',
          };

          return newState;
        }

        if (!state.birthDate) {
          newState = {
            ...state,
            registerError: 'register.birthdateRequired',
          };

          return newState;
        }

        if (state.birthDate.isAfter(moment().subtract(16, 'years'))) {
          newState = {
            ...state,
            registerError: 'register.birthdateTooYoung',
          };

          return newState;
        }

        if (!fullnameError && state.firstName && state.lastName) {
          newState = {
            ...state,
            loading: true,
            fullnameError,
          };

          registerUser(state);

          return newState;
        }

        newState = {
          ...state,
          fullnameError,
        };

        return newState;
      }
      case Types.RegisterSubmissionError: {
        newState = {
          ...state,
          loading: false,
          registerSubmissionError: (action.payload as ErrorPayload).error,
        };

        return newState;
      }
      case Types.RegisterCodeError: {
        newState = {
          ...state,
          loading: false,
          registerCodeError: 'register.codeError',
          showVerification: true,
          verificationReregister: true,
          verificationCode: '',
          showRegister: false,
        };

        return newState;
      }
      case Types.LocationSelected: {
        const { latLon } = action.payload as LocationSelectedPayload;

        newState = {
          ...state,
          latLon,
        };

        return newState;
      }
      case Types.UserGeolocationChange: {
        const { geolocation } = action.payload as UserGeolocationChangePayload;
        newState = {
          ...state,
          geolocation,
        };

        return newState;
      }
      case Types.ShowOnboarding: {
        const { result } = action.payload as ShowOnboardingPayload;

        axiosInstance.defaults.headers.common.Authorization = `Bearer ${result.payload?.jwtToken}`;

        newState = {
          ...state,
          showOnboarding: true,
          accessToken: result.payload?.jwtToken,
          loading: false,
        };

        return newState;
      }
      case Types.EducationSelect: {
        const { education } = action.payload as EducationSelectedPayload;
        newState = {
          ...state,
          education,
        };

        return newState;
      }
      case Types.LanguageLevelsSelect: {
        const { languageLevels } = action.payload as LanguageLevelsSelectedPayload;
        newState = {
          ...state,
          languageLevels,
        };

        return newState;
      }
      case Types.MilitaryStatusSelect: {
        const { militaryStatus } = action.payload as MilitaryStatusSelectedPayload;
        newState = {
          ...state,
          militaryStatus,
        };

        return newState;
      }
      case Types.DrivingLicencesSelect: {
        const { drivingLicences } = action.payload as DrivingLicencesSelectedPayload;
        newState = {
          ...state,
          drivingLicences,
        };

        return newState;
      }
      case Types.CurrentWorkStatusSelect: {
        const { currentWorkStatus } = action.payload as CurrentWorkStatusSelectedPayload;
        newState = {
          ...state,
          currentWorkStatus,
        };

        return newState;
      }
      case Types.WorkTimePreferencesSelect: {
        const { workTimePreferences } = action.payload as WorkTimePreferencesSelectedPayload;
        newState = {
          ...state,
          workTimePreferences,
        };

        return newState;
      }
      case Types.ExpectedSalarySelect: {
        const { expectedSalary } = action.payload as ExpectedSalarySelectedPayload;
        newState = {
          ...state,
          expectedSalary,
        };

        return newState;
      }
      case Types.PersonInfosSubmitError: {
        const { error } = action.payload as PersonInfosSubmitErrorPayload;
        newState = {
          ...state,
          personalInfosSubmitError: error,
          loading: false,
        };
        return newState;
      }
      case Types.PersonalInfosSubmitted: {
        const { formConfig } = action.payload as PersonalInfosSubmittedPayload;
        state.personalInfosSubmitError = undefined;
        newState = {
          ...state,
          loading: true,
        };

        const validateAndDispatch = (field: string, errorKey: string, condition: boolean) => {
          if (isFieldRequired(formConfig, field) && condition) {
            newState = {
              ...state,
              personalInfosSubmitError: `register.${errorKey}Required`,
              loading: false,
            };
            return newState;
          }
        };

        validateAndDispatch('personalInformation', 'education', state.education === undefined);
        validateAndDispatch(
          'personalInformation',
          'englishLevel',
          !state.languageLevels || state.languageLevels.length < 1,
        );
        validateAndDispatch(
          'personalInformation',
          'militaryStatus',
          !state.militaryStatus && state.gender === Gender.MALE,
        );
        validateAndDispatch(
          'personalInformation',
          'drivingLicence',
          state.drivingLicences === undefined,
        );
        validateAndDispatch(
          'personalInformation',
          'currentWorkStatus',
          state.currentWorkStatus === undefined,
        );
        validateAndDispatch(
          'workTimePreferences',
          'workTimePreferences',
          !state.workTimePreferences || state.workTimePreferences?.length < 1,
        );
        validateAndDispatch('expectedSalary', 'expectedSalary', !state.expectedSalary);

        if (newState.personalInfosSubmitError) {
          return newState;
        }

        updateUser(state);

        return newState;
      }
      default:
        throw new Error();
    }
  }, initialState.state);

  return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

export { userStore, UserStateProvider };
